/**
 * Bunker building by supply trucks.
 * Two-turn action: turn 1 creates a ghost bunker (buildProgress=1),
 * turn 2 completes it (buildProgress→0). If the builder leaves, the ghost is removed.
 */
import type { TurnWorld, Unit, Building } from './types';
import { getBuildingAt } from './capture';
import { BUILDABLE_BUILDING_TERRAINS } from './terrain';

/**
 * Terrain types where bunkers can be built. Buildings may only sit on base
 * (land) terrain — never on object terrains like road/forest/mountain/bridge.
 * Water is a base terrain but not buildable for a land structure.
 */
const BUILDABLE_TERRAIN = BUILDABLE_BUILDING_TERRAINS;

/**
 * Check if a supply truck can start building bunkeres at its current position.
 * Requires: supply-truck, not acted, plains/road terrain, no existing building.
 */
export function canBuild(world: TurnWorld, unit: Unit): boolean {
  if (unit.type !== 'supply-truck') return false;
  if (unit.acted) return false;

  const terrain = world.map.tiles[unit.row][unit.col].type;
  if (!BUILDABLE_TERRAIN.has(terrain)) return false;

  const existing = getBuildingAt(world, unit.col, unit.row);
  if (existing !== null) return false;

  return true;
}

/**
 * Find an in-progress bunker being built by this unit's owner at the unit's position.
 */
export function getBuildInProgress(world: TurnWorld, unit: Unit): Building | null {
  if (unit.type !== 'supply-truck') return null;

  for (const [, b] of world.buildings) {
    if (
      b.col === unit.col && b.row === unit.row &&
      b.type === 'bunker' &&
      b.buildProgress > 0 &&
      b.buildingPlayerId === unit.owner
    ) {
      return b;
    }
  }
  return null;
}

/**
 * Check if a supply truck can issue a build order — either start new or continue.
 */
export function canBuildOrContinue(world: TurnWorld, unit: Unit): boolean {
  if (unit.type !== 'supply-truck') return false;
  if (unit.acted) return false;

  if (getBuildInProgress(world, unit) !== null) return true;
  return canBuild(world, unit);
}

/**
 * Execute build action.
 * First call: creates ghost bunker with buildProgress=1.
 * Second call: completes the bunker (buildProgress→0, fully built).
 * Returns true if construction completed.
 */
export function executeBuild(world: TurnWorld, unit: Unit): boolean {
  if (unit.type !== 'supply-truck') return false;

  // Continue existing build
  const building = getBuildInProgress(world, unit);
  if (building) {
    building.buildProgress += 1;
    unit.acted = true;
    unit.movePoints = 0;

    if (building.buildProgress >= 2) {
      building.buildProgress = 0;
      building.buildingPlayerId = null;
      return true;
    }
    return false;
  }

  // Start new build — check conditions directly (unit.acted may already be
  // set by the UI handler before the animation callback, like executeCapture).
  const terrain = world.map.tiles[unit.row][unit.col].type;
  if (!BUILDABLE_TERRAIN.has(terrain)) return false;
  const existing = getBuildingAt(world, unit.col, unit.row);
  if (existing !== null) return false;

  const newBuilding: Building = {
    id: world.nextId++,
    type: 'bunker',
    owner: null,
    col: unit.col,
    row: unit.row,
    captureProgress: 0,
    capturingPlayer: null,
    training: null,
    buildProgress: 1,
    buildingPlayerId: unit.owner,
  };
  world.buildings.set(newBuilding.id, newBuilding);
  unit.acted = true;
  unit.movePoints = 0;

  return false;
}

/**
 * Remove incomplete ghost bunkeres whose builder is no longer on the tile.
 * Call alongside validateCaptures after moves, attacks, loads, etc.
 */
export function validateBuilds(world: TurnWorld): void {
  const toRemove: number[] = [];

  for (const [id, building] of world.buildings) {
    if (building.buildingPlayerId === null || building.buildProgress === 0) continue;

    let found = false;
    for (const [, unit] of world.units) {
      if (
        unit.type === 'supply-truck' &&
        unit.owner === building.buildingPlayerId &&
        unit.col === building.col &&
        unit.row === building.row
      ) {
        found = true;
        break;
      }
    }

    if (!found) {
      toRemove.push(id);
    }
  }

  for (const id of toRemove) {
    world.buildings.delete(id);
  }
}
